import React, { useEffect, useState } from "react";
import { editIconInBox } from "../../../assets";
import WrapperCard from "../../../component-lib/Atoms/card/EventEHRWrapperCard";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import useDeviceType from "../../../custom-hooks/DeviceType";
import DischargeFormPreviewModel from "../../../utils/discharge-form-preview-model/DischargeFormPreviewModel";
import EventEHRVitalsCard from "../../../component-lib/Molecules/card/EventEHRVitalsCard";
import Input from "../../../component-lib/Atoms/Input/Input";
import EventEHRPatientProfileDetailCard from "../../../component-lib/Organisms/patient-profile-detail-card/EventEHRPatientProfileDetailCard";
import DispositionDialog from "./dialogs/DispositionDialog";
import IntervalNotesSection from "./IntervalNotesSection";
import EventEHRCareVisitFlowButtons from "../../../component-lib/Molecules/care-visit-flow-buttons/EventEHRCareVisitFlowButtons";
import {
  COMMON_COLOR565656,
  COMMON_FONT16_500,
  COMMON_MARGIN_BOTTOM_8PX,
} from "./constant";
import { useDispatch, useSelector } from "react-redux";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  getVisitNoteById,
  patchVisitNoteData,
  postVisitNoteData,
} from "../patientSaga";
import {
  componentKey,
  setCloseChiefComplaintDialog,
  setCloseCreateTriageDrawer,
  setCloseDispositionDialog,
  setDataToDischargeForm,
  setUpdateVisitNoteDataOnceDraftNote,
} from "../patientSlice";
import {
  decideStatusOnButtonClick,
  getPatientDetailsDataFunction,
  getTriageDetailsDataFunction,
  setVisitNoteInfoDisable,
} from "../constants";
import { isOffsiteTypeOfCare } from "../../../utils/offsite/isOffsite";
import EventEHREditChiefComplaintDialog from "./dialogs/edit-chief-complaint-dialog/EventEHREditChiefComplaintDialog";
import { eventEHRCustomToastMessage } from "../../../component-lib/Atoms/custom-toast-message/EventEHRCustomToastMessage";
import TriageHistoryDialog from "./dialogs/TriageHistoryDialog";
import SaveTriageDialog from "./SaveTriageDialog";
import { createTriageClick } from "../create-patient-drawer/constants";
import MultipleSelectWithEvents from "../../../component-lib/Atoms/Select/MultiSelect";
import {
  ENCOUNTER_TOPIC_OPTIONS,
  ESCORT_LEGEND,
  RESOLUTION_OPTIONS,
} from "./listeningEarConstants";

const ListeningEarCareVisitFlow = () => {
  const {
    visitNoteCareFlowData,
    closeChiefComplaintDialog,
    updateVisitNoteDataOnceDraftNote,
    closeDispositionDialog,
    notesData,
    closeTriageDrawer,
  } = useSelector((state: any) => state[componentKey]);

  let index = visitNoteCareFlowData?.visit_note?.findIndex(
    (obj: any) => obj.id === notesData.id,
  );
  // Set index to last  element if not found

  if (index === -1) {
    index = visitNoteCareFlowData?.visit_note?.length - 1;
  }

  const [searchParams] = useSearchParams();
  const isAddNewNote = searchParams.get("isAddNewNote"); // Replace 'yourParam' with your parameter name
  const newNoteStatus = searchParams.get("newNoteStatus"); // Replace 'yourParam' with your parameter name
  const [addNewNote, setAddNewNote] = useState(isAddNewNote || false);

  const isPatientTransfer = isAddNewNote
    ? false
    : setVisitNoteInfoDisable(visitNoteCareFlowData?.status);

  const { pathname } = useLocation();
  // Offsite/hospital visits reuse this flow but get the interval-note action and
  // the hospital-specific disposition options. Real Listening-Ear visits do not.
  // EMS/off-property visits carry no typeOfCare, so also treat anything mounted
  // on the offsite-transfers route as offsite.
  const isOffsite =
    pathname.includes("offsite-transfers") ||
    isOffsiteTypeOfCare(visitNoteCareFlowData?.typeOfCare);

  const patientDetailsData = visitNoteCareFlowData;
  const triageDetailsData = visitNoteCareFlowData?.triage;

  const { patientId } = useParams();
  const navigate = useNavigate();
  const { visitNoteId } = useParams();
  // This component is also mounted for the Offsite Transfers route. The
  // Encounter Topic + Resolution fields apply to Listening Ear ONLY, so gate
  // all new behavior behind this flag — Offsite stays byte-for-byte unchanged.
  const isListeningEar = pathname.includes(
    "view-listening-ear-care-visit-note"
  );
  const dispatch = useDispatch();
  const deviceType = useDeviceType();
  const [openDischargeForm, setOpenDischargeForm] = useState(false);
  const [openDispositionDialog, setOpenDispositionDialog] = useState(false);
  const [openChiefComplaintDialog, setOpenChiefComplaintDialog] =
    useState(false);
  const [selectedDispositionKey, setSelectedDispositionKey] = useState("");
  const [dischargeNote, setDischargeNote] = useState("");

  const [openTriageHistory, setOpenTriageHistory] = useState({
    isOpen: false,
    data: [],
  });
  const [openTriage, setOpenTriage] = useState(false);

  // FORM DATA
  const [formFieldData, setFormFieldData] = useState<{
    subjectiveNote: string;
    encounterTopic: string[];
  }>({
    subjectiveNote: "",
    encounterTopic: [],
  });
  // Interval notes (offsite): one field shows the first time; "Add More"
  // reveals the next field; the footer "File Interval Note" button commits the
  // typed note (record stays open).
  const [intervalNoteText, setIntervalNoteText] = useState("");
  const [showNewField, setShowNewField] = useState(false);

  // The current saved visit note (interval notes attach to it).
  const currentVisitNote = visitNoteCareFlowData?.visit_note?.[index];
  // Interval notes can live on any of the visit's notes (typically the earlier
  // "InInterval" note), so aggregate across all of them and sort by time.
  const intervalNotes = (visitNoteCareFlowData?.visit_note || [])
    .flatMap((vn: any) => vn?.intervalNotes || [])
    .sort(
      (a: any, b: any) =>
        new Date(a?.createdAt).getTime() - new Date(b?.createdAt).getTime(),
    );

  const handleFileIntervalNote = () => {
    if (!intervalNoteText || intervalNoteText.trim().length === 0) {
      eventEHRCustomToastMessage({
        title: `Please enter an interval note.`,
        status: "error",
      });
      return;
    }
    // File the interval note as a new entry (record stays open, no disposition).
    // If no visit note exists yet, create one carrying the interval note so it
    // can be added any time.
    // Persist any documentation entered so far (mirrors the draft-save fields)
    // so it isn't lost when filing the interval note.
    const soapDraftPayload = {
      subjectiveNote: formFieldData?.subjectiveNote || undefined,
      ...(isListeningEar
        ? { encounterTopic: formFieldData?.encounterTopic }
        : {}),
    };
    const apiPayload = {
      status: "InInterval",
      ...soapDraftPayload,
      intervalNotes: [{ note: intervalNoteText.trim() }],
    };
    if (currentVisitNote?.id) {
      dispatch(
        patchVisitNoteData({
          apiPayload,
          comesFrom: "intervalNote",
          visitNoteId: currentVisitNote?.id,
          patientId,
          navigate,
        }),
      );
    } else {
      dispatch(
        postVisitNoteData({
          apiPayload: { ...apiPayload, patientId, visitId: visitNoteId },
          comesFrom: "intervalNote",
          patientId,
          navigate,
        }),
      );
    }
    setIntervalNoteText("");
    setShowNewField(false);
  };

  // TRIAGE CARD DATA
  const triageCardData = getTriageDetailsDataFunction(triageDetailsData);

  // PATIENT DETAILS DATA
  const patientDetails = getPatientDetailsDataFunction(patientDetailsData);

  // CLOSE DISPOSITION DIALOG
  useEffect(() => {
    if (closeDispositionDialog) {
      dispatch(setCloseDispositionDialog(false));
      setOpenDispositionDialog(false);
      setOpenDischargeForm(true);
    }
  }, [closeDispositionDialog]);

  useEffect(() => {
    if (closeChiefComplaintDialog) {
      setOpenChiefComplaintDialog(false);
      dispatch(setCloseChiefComplaintDialog(false));
    }
    if (updateVisitNoteDataOnceDraftNote) {
      dispatch(setUpdateVisitNoteDataOnceDraftNote(false));
    }
    if (closeTriageDrawer) {
      setOpenTriage(false);
      dispatch(setCloseCreateTriageDrawer(false));
    }
    const payload = {
      id: visitNoteId,
      comesFrom: "lecCare",
    };
    dispatch(getVisitNoteById(payload));
  }, [
    closeChiefComplaintDialog,
    updateVisitNoteDataOnceDraftNote,
    closeDispositionDialog,
    closeTriageDrawer,
  ]);

  // UPDATE DRAFT DATA IF EXISTS
  useEffect(() => {
    if (
      !addNewNote ||
      newNoteStatus === "InDisposition" ||
      newNoteStatus === "Visit Created" ||
      newNoteStatus === "Draft" ||
      newNoteStatus === "InInterval"
    ) {
      setFormFieldData((prevData: any) => ({
        ...prevData,
        subjectiveNote:
          prevData?.subjectiveNote ||
          visitNoteCareFlowData?.visit_note?.[index]?.subjectiveNote ||
          "",
        encounterTopic:
          prevData?.encounterTopic?.length
            ? prevData.encounterTopic
            : visitNoteCareFlowData?.visit_note?.[index]?.encounterTopic || [],
      }));
    }
  }, [visitNoteCareFlowData]);

  useEffect(() => {
    dispatch(
      setDataToDischargeForm({
        ...visitNoteCareFlowData,
        assessment: {},
      }),
    );
  }, [visitNoteCareFlowData]);

  // DRAFT VISIT NOTE DATA
  const handleButtonClick = (status: any, extraParams?: string) => {
    // Offsite (interval-note-driven) discharge: the subjective note is NOT
    // required when an interval note is present (typed or already filed).
    // Listening Ear keeps requiring a subjective note.
    const pendingInterval = intervalNoteText?.trim();
    const hasIntervalNote =
      (pendingInterval?.length || 0) > 0 || (intervalNotes?.length || 0) > 0;
    if (!(isOffsite && hasIntervalNote)) {
      if (
        !formFieldData?.subjectiveNote ||
        formFieldData?.subjectiveNote?.trim().length === 0
      ) {
        eventEHRCustomToastMessage({
          title: `Please select subjective note.`,
          status: "error",
        });
        return;
      }
    }
    // Vitals are optional for Listening Ear visits (no mandatory triage).
    // Carry an un-filed interval note into this save so it isn't lost.
    const intervalNotePayload = pendingInterval
      ? { intervalNotes: [{ note: pendingInterval }] }
      : {};
    // Open the disposition dialog only after the save succeeds (passed to the
    // saga as onSuccess). Undefined for Draft saves so they never open it.
    const onSuccess =
      status !== "Draft"
        ? () => setOpenDispositionDialog(true)
        : extraParams === "draftSave"
          ? () => navigate(`/patient/${patientId}?tab=visits`)
          : undefined;
    try {
      if (visitNoteCareFlowData?.visit_note.length === 0) {
        const payload = {
          apiPayload: {
            subjectiveNote: formFieldData?.subjectiveNote || undefined,
            patientId: patientId,
            visitId: visitNoteId,
            status: status,
            ...(isListeningEar && {
              encounterTopic: formFieldData?.encounterTopic,
            }),
            ...intervalNotePayload,
          },
          comesFrom: "",
          navigate,
          onSuccess,
        };
        dispatch(postVisitNoteData(payload));
      } else if (
        addNewNote &&
        // An interval note already created this visit note (status "InInterval"),
        // so finalize it with a PATCH instead of creating a duplicate note.
        currentVisitNote?.status !== "InInterval" &&
        !(
          newNoteStatus === "InDisposition" ||
          newNoteStatus === "Visit Created" ||
          newNoteStatus === "Draft"
        )
      ) {
        setAddNewNote(false);
        const payload = {
          apiPayload: {
            subjectiveNote: formFieldData?.subjectiveNote || undefined,
            patientId: patientId,
            visitId: visitNoteId,
            status: status,
            ...(isListeningEar && {
              encounterTopic: formFieldData?.encounterTopic,
            }),
            ...intervalNotePayload,
          },
          comesFrom: "",
          navigate,
          onSuccess,
        };
        dispatch(postVisitNoteData(payload));
      } else {
        const payload = {
          apiPayload: {
            subjectiveNote: formFieldData?.subjectiveNote || undefined,
            status: status,
            ...(isListeningEar && {
              encounterTopic: formFieldData?.encounterTopic,
            }),
            ...intervalNotePayload,
          },
          comesFrom: "",
          visitNoteId: visitNoteCareFlowData?.visit_note?.[index]?.id,
          navigate,
          onSuccess,
        };
        dispatch(patchVisitNoteData(payload));
      }
      if (pendingInterval) {
        setIntervalNoteText("");
      }
      dispatch(
        setDataToDischargeForm({
          ...visitNoteCareFlowData,
          assessment: {},
        }),
      );
      // The disposition dialog is now opened by `onSuccess` from the saga, only
      // after the visit-note save succeeds (not here, regardless of result).
    } catch (error) {
      console.log("error:", error);
    }
  };

  return (
    <WrapperCard
      customClasses="m-3 p-3 pl-4 pr-4 flex-1 min-h-0 overflow-auto"
      customStyle={{ minWidth: deviceType !== "mobile" ? "400px" : "" }}
    >
      <div className="flex flex-col justify-between gap-3 h-full">
        <div className="flex flex-col justify-between gap-3 overflow-hidden">
          {/* HEADING */}
          <Heading title="Visit Note" customStyle={{ color: "#1D1D1D" }} />
          {/* MAIN CONTENT */}
          <div className="pr-2 overflow-auto">
            {/* PROFILE INFO CARD */}
            <div className="flex background-color-F1F1F1 border-radius-8px py-4 px-6">
              <EventEHRPatientProfileDetailCard
                patientDetails={patientDetails}
                patientId={patientId}
              />
            </div>
            {/* SUBJECTIVE */}
            <div className="flex flex-col pt-3">
              <div className="flex justify-between items-center mb-2">
                <Heading
                  title="Chief Complaint"
                  customStyle={{
                    ...COMMON_FONT16_500,
                    ...COMMON_COLOR565656,
                  }}
                />
                {!isPatientTransfer && (
                  <img
                    alt="edit-icon"
                    src={editIconInBox}
                    width={"36px"}
                    height={"36px"}
                    className="cursor-pointer"
                    onClick={() => {
                      setOpenChiefComplaintDialog(true);
                    }}
                  />
                )}
              </div>
              <div>
                <div
                  className="font-weight-500 border-radius-8px color-979797"
                  style={{
                    padding: "14px 16px",
                    border: "1px solid #D2D2D2",
                    background: "#F8F8F8",
                  }}
                >
                  {visitNoteCareFlowData?.chiefComplaints}
                </div>
                {/* ENCOUNTER TOPIC (Listening Ear only) */}
                {isListeningEar && (
                  <div className="mt-3">
                    <Heading
                      title="Encounter Topic"
                      customStyle={{
                        ...COMMON_FONT16_500,
                        ...COMMON_COLOR565656,
                        ...COMMON_MARGIN_BOTTOM_8PX,
                      }}
                    />
                    <MultipleSelectWithEvents
                      label="Select Encounter Topic"
                      options={ENCOUNTER_TOPIC_OPTIONS}
                      renderAsChips
                      defaultSelectedValues={formFieldData.encounterTopic}
                      onSelectionChanged={(selectedValues) => {
                        if (isPatientTransfer) return;
                        setFormFieldData((prevData: any) => ({
                          ...prevData,
                          encounterTopic: selectedValues,
                        }));
                      }}
                    />
                    <div
                      className="color-979797 mt-1"
                      style={{ fontSize: "12px" }}
                    >
                      {ESCORT_LEGEND}
                    </div>
                  </div>
                )}
                {/* HISTORY OF PRESENT ILLNESS FIELD  */}
                <div className="mt-2">
                  <Heading
                    title="Subjective Note"
                    isRequired={!isOffsite}
                    customStyle={{
                      ...COMMON_FONT16_500,
                      ...COMMON_COLOR565656,
                      ...COMMON_MARGIN_BOTTOM_8PX,
                    }}
                  />
                  <Input
                    isStarPresent={isPatientTransfer}
                    rows={3}
                    size="small"
                    placeholder="Enter Subjective Note"
                    multiline={true}
                    maxWords={250}
                    name="subjectiveNote"
                    value={formFieldData.subjectiveNote}
                    onChangeCb={(e: any) => {
                      setFormFieldData((prevData: any) => ({
                        ...prevData,
                        subjectiveNote: e?.target?.value,
                      }));
                    }}
                  />
                </div>
              </div>
            </div>
            {/* INTERVAL NOTES (moved to bottom — offsite/hospital only) */}
            {isOffsite && (
              <div className="flex flex-col pt-3">
                <Heading
                  title="Interval Notes (optional)"
                  customStyle={{
                    fontWeight: 500,
                    color: "#1D1D1D",
                    fontSize: "16px",
                    marginBottom: "8px",
                  }}
                />
                <IntervalNotesSection
                  intervalNotes={intervalNotes}
                  isReadOnly={isPatientTransfer}
                  noteText={intervalNoteText}
                  onNoteTextChange={setIntervalNoteText}
                  showNewField={showNewField}
                  onAddMore={() => setShowNewField(true)}
                />
              </div>
            )}
          </div>
        </div>
        {/* FOOTER BUTTONS */}
        {!isPatientTransfer && (
          <EventEHRCareVisitFlowButtons
            isTransferButtonPresent={false}
            isFileIntervalNotePresent={isOffsite}
            handleCancelButtonClickEvent={() => {
              navigate(`/patient/${patientId}?tab=visits`);
            }}
            handleSaveAsDraftButtonClickEvent={() => {
              handleButtonClick("Draft", "draftSave");
            }}
            handleFileIntervalNoteButtonClickEvent={handleFileIntervalNote}
            handleProceedToDischargeButtonClickEvent={() => {
              const status = decideStatusOnButtonClick(visitNoteCareFlowData);
              handleButtonClick(status);
              // setOpenDischargeForm(true);
            }}
          />
        )}
      </div>
      {openDischargeForm && (
        <DischargeFormPreviewModel
          patientId={patientId}
          visitNoteId={visitNoteId}
          selectedDispositionKey={selectedDispositionKey}
          dischargeNote={dischargeNote}
          open={openDischargeForm}
          comesFrom={selectedDispositionKey !== "discharged" ? "soapNote" : ""}
          handleCloseModel={() => setOpenDischargeForm(false)}
        />
      )}
      {openDispositionDialog && (
        <DispositionDialog
          patientId={patientId}
          visitNoteId={visitNoteCareFlowData?.visit_note?.[index]?.id}
          isOffsite={isOffsite}
          open={openDispositionDialog}
          isResolution={isListeningEar}
          handleCloseDialog={() => {
            setOpenDispositionDialog(false);
          }}
          handleDischargeClick={(type, data) => {
            if (type === "discharged") {
              setSelectedDispositionKey("discharged");
              setDischargeNote(data);

              setOpenDischargeForm(true);
            }
          }}
        />
      )}
      {/* EDIT CHIEF COMPLAINT DIALOG */}
      {openChiefComplaintDialog && (
        <EventEHREditChiefComplaintDialog
          defaultSelectedData={visitNoteCareFlowData?.chiefComplaints}
          dialogHeading="Edit Chief Complaint"
          isOpen={openChiefComplaintDialog}
          handleClose={() => {
            setOpenChiefComplaintDialog(false);
          }}
        />
      )}
    </WrapperCard>
  );
};

export default ListeningEarCareVisitFlow;
